import { useEffect, useRef, useState, type ReactNode } from 'react';

/* Palco do HUD.
   No computador, a composição é desenhada numa prancheta de 1600×860 e escalada
   para ocupar a janela inteira: a prancheta cresce no eixo que sobrar, e os
   painéis se ancoram às bordas, então nada fica cortado nem com faixas vazias.
   No celular (ou janela estreita), vira uma coluna rolável. */

const BASE_W = 1600;
const BASE_H = 860;

function measure() {
  const vw = window.innerWidth, vh = window.innerHeight;
  const compact = vw < 980 || vh < 560;
  const scale = compact ? 1 : Math.min(vw / BASE_W, vh / BASE_H);
  return { compact, scale, w: vw / scale, h: vh / scale };
}

export function useStage() {
  const [m, setM] = useState(measure);
  useEffect(() => {
    const on = () => setM(measure());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return m;
}

export type Trace = [number, number][];

export function HudStage({ children, className = '', label, traces }: {
  children: ReactNode; className?: string; label: string;
  /* Linhas de circuito ligando os painéis ao centro, em coordenadas da prancheta. */
  traces?: (w: number, h: number) => Trace[];
}) {
  const m = useStage();
  return (
    <div className={`hx-viewport ${m.compact ? 'is-compact' : 'is-wide'}`}>
      <Backdrop />
      <main id="main" tabIndex={-1} aria-label={label}
        className={`hx-art ${className}`}
        style={m.compact ? undefined : { width: m.w, height: m.h, transform: `scale(${m.scale})` }}>
        <Frame />
        {traces && !m.compact && <Circuits paths={traces(m.w, m.h)} w={m.w} h={m.h} />}
        {children}
      </main>
    </div>
  );
}

function Circuits({ paths, w, h }: { paths: Trace[]; w: number; h: number }) {
  return (
    <svg className="hx-circuits" viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      {paths.map((p, i) => {
        const d = p.map(([x, y], k) => `${k ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join('');
        const [sx, sy] = p[0]!;
        const [ex, ey] = p[p.length - 1]!;
        return (
          <g key={i}>
            <path d={d} />
            <path className="pk" d={d} style={{ animationDelay: `-${(i * 0.73) % 4.5}s` }} />
            <circle cx={sx} cy={sy} r="2.5" />
            <circle className="f" cx={ex} cy={ey} r="3" />
          </g>
        );
      })}
    </svg>
  );
}

/* Moldura em colchetes, como a borda de um visor. */
export function Frame() {
  return (
    <div className="hx-frame" aria-hidden="true">
      <i className="fr tl" /><i className="fr tr" /><i className="fr bl" /><i className="fr br" />
      <i className="fr-side l" /><i className="fr-side r" />
      <i className="fr-bar top" /><i className="fr-bar bottom" />
    </div>
  );
}

/* Fundo: gradiente profundo, raios de luz, piso em perspectiva, luz que segue o
   ponteiro e um campo de partículas desfocadas com paralaxe. */
export function Backdrop() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cv = ref.current;
    const ctx = cv?.getContext('2d');
    if (!cv || !ctx) return;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    let w = 0, h = 0, raf = 0, last = 0, mx = 0, my = 0, tmx = 0, tmy = 0;

    const sprite = document.createElement('canvas');
    sprite.width = sprite.height = 64;
    const sg = sprite.getContext('2d')!;
    const grad = sg.createRadialGradient(32, 32, 0, 32, 32, 32);
    grad.addColorStop(0, 'rgba(120,210,255,0.9)');
    grad.addColorStop(0.35, 'rgba(60,150,255,0.35)');
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    sg.fillStyle = grad;
    sg.fillRect(0, 0, 64, 64);

    const bokeh = Array.from({ length: 42 }, () => ({
      x: Math.random(), y: Math.random(), z: 0.2 + Math.random() * 0.8,
      s: 10 + Math.random() * 46, v: 0.002 + Math.random() * 0.006, ph: Math.random() * 6.28
    }));
    const stars = Array.from({ length: 140 }, () => ({ x: Math.random(), y: Math.random(), ph: Math.random() * 6.28, z: Math.random() }));

    const size = () => {
      const dpr = Math.min(1.5, window.devicePixelRatio || 1);
      w = window.innerWidth; h = window.innerHeight;
      cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    size();
    window.addEventListener('resize', size);
    const onMove = (e: PointerEvent) => { tmx = e.clientX / w - 0.5; tmy = e.clientY / h - 0.5; };
    window.addEventListener('pointermove', onMove, { passive: true });

    let t = 0;
    const draw = (now: number) => {
      raf = requestAnimationFrame(draw);
      if (now - last < 33) return;
      const dt = last ? Math.min(0.1, (now - last) / 1000) : 0.033;
      last = now;
      t += dt;
      mx += (tmx - mx) * 0.05; my += (tmy - my) * 0.05;
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';
      for (const b of bokeh) {
        b.y -= b.v * dt * 6;
        if (b.y < -0.1) { b.y = 1.1; b.x = Math.random(); }
        const x = b.x * w - mx * 60 * b.z, y = b.y * h - my * 40 * b.z;
        ctx.globalAlpha = (0.05 + 0.1 * b.z) * (0.7 + 0.3 * Math.sin(t + b.ph));
        ctx.drawImage(sprite, x - b.s / 2, y - b.s / 2, b.s, b.s);
      }
      ctx.globalAlpha = 1;
      for (const s of stars) {
        const a = 0.15 + 0.5 * Math.max(0, Math.sin(t * (0.6 + s.z) + s.ph));
        ctx.fillStyle = `rgba(170,225,255,${a.toFixed(3)})`;
        ctx.fillRect(s.x * w - mx * 20 * s.z, s.y * h - my * 14 * s.z, 1.2, 1.2);
      }
      ctx.globalCompositeOperation = 'source-over';
      if (reduce) cancelAnimationFrame(raf);
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', size);
      window.removeEventListener('pointermove', onMove);
    };
  }, []);

  return (
    <div className="hb" aria-hidden="true">
      <div className="hb-base" />
      <div className="hb-rays" />
      <div className="hb-floor"><div className="hb-grid" /></div>
      <canvas ref={ref} className="hb-bokeh" />
      <div className="hb-spot" />
      <div className="hb-scan" />
      <div className="hb-vig" />
    </div>
  );
}
